import { useState, useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { useParty } from '@/hooks/useParty'
import { useCreateAction } from '@/hooks/useCreateAction'
import {
  calculateDonorAlignment,
  estimateFundraiseYield,
  getAlignmentBarColor,
} from '@/lib/actionEffects'
import {
  DONOR_BLOCS,
  DONOR_BLOC_ORDER,
  FUNDRAISE_INTENSITY_LABELS,
  type DonorBlocKey,
} from '@/constants/donors'
import { cn } from '@/lib/utils'

interface FundraiseActionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partyId: string
  currentTick: number
  availableAP: number
  onSuccess?: () => void
  onBack?: () => void
}

export function FundraiseActionForm({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: FundraiseActionFormProps) {
  if (!open) return null

  return (
    <FundraiseActionFormContent
      open={open}
      onOpenChange={onOpenChange}
      partyId={partyId}
      currentTick={currentTick}
      availableAP={availableAP}
      onSuccess={onSuccess}
      onBack={onBack}
    />
  )
}

function FundraiseActionFormContent({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: FundraiseActionFormProps) {
  // Form state
  const [selectedBloc, setSelectedBloc] = useState<DonorBlocKey | null>(null)
  const [intensity, setIntensity] = useState(1)

  // Fetch party data for ideology and momentum
  const { party, isLoading: partyLoading } = useParty(partyId)

  // Create action mutation
  const { createAction, isCreating, error } = useCreateAction({
    onSuccess: () => {
      onSuccess?.()
      onOpenChange(false)
    },
  })

  // Calculate alignment for each donor bloc
  const blocAlignments = useMemo((): Record<DonorBlocKey, number> => {
    if (!party?.ideology_vector) {
      // Return default alignments when party data not loaded
      return {
        business: 50,
        labor: 50,
        grassroots: 50,
        wealthy: 50,
      }
    }

    return DONOR_BLOC_ORDER.reduce(
      (acc, blocKey) => {
        acc[blocKey] = calculateDonorAlignment(
          party.ideology_vector,
          blocKey,
          party.momentum
        )
        return acc
      },
      {} as Record<DonorBlocKey, number>
    )
  }, [party])

  // Calculate estimated yield for selected bloc
  const yieldEstimate = useMemo(() => {
    if (!selectedBloc || !blocAlignments[selectedBloc]) return null
    return estimateFundraiseYield(
      selectedBloc,
      blocAlignments[selectedBloc],
      intensity
    )
  }, [selectedBloc, blocAlignments, intensity])

  // Validation
  const canSubmit =
    !isCreating &&
    selectedBloc !== null &&
    intensity <= availableAP

  const apAfterAction = availableAP - intensity

  const handleSubmit = () => {
    if (!canSubmit || !selectedBloc || !yieldEstimate) return

    createAction({
      party_id: partyId,
      tick: currentTick,
      action_type: 'fundraise',
      action_subtype: selectedBloc,
      target_type: 'national',
      target_id: undefined,
      action_points_spent: intensity,
      parameters: {
        donor_bloc: selectedBloc,
        intensity,
        estimated_yield: yieldEstimate.expected,
      },
    })
  }

  const handleBack = () => {
    onBack?.()
    onOpenChange(false)
  }

  // Format currency for display
  const formatMoney = (amount: number) => {
    return '$' + amount.toLocaleString()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>💰</span> Configure Fundraising
          </DialogTitle>
          <DialogDescription>
            Solicit donations from supporter groups aligned with your party's
            values.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Donor Bloc Selection */}
          <div className="space-y-3">
            <Label>Select Donor Bloc</Label>
            {partyLoading ? (
              <div className="flex h-32 items-center justify-center">
                <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {DONOR_BLOC_ORDER.map((blocKey) => {
                  const bloc = DONOR_BLOCS[blocKey]
                  const alignment = blocAlignments[blocKey] ?? 0
                  const isSelected = selectedBloc === blocKey

                  return (
                    <button
                      key={blocKey}
                      type="button"
                      onClick={() => setSelectedBloc(blocKey)}
                      className={cn(
                        'rounded-lg border-2 p-3 text-left transition-all',
                        'hover:border-primary/50 hover:bg-accent/50',
                        isSelected
                          ? 'border-primary bg-accent'
                          : 'border-border bg-background'
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{bloc.icon}</span>
                          <span className="font-medium">{bloc.label}</span>
                        </div>
                        {isSelected && (
                          <div className="bg-primary text-primary-foreground rounded-full p-0.5">
                            <svg
                              className="h-3 w-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {bloc.description}
                      </p>

                      {/* Alignment bar */}
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">
                            Alignment
                          </span>
                          <span className="font-medium">{alignment}%</span>
                        </div>
                        <div className="bg-secondary h-1.5 w-full overflow-hidden rounded-full">
                          <div
                            className={cn(
                              'h-full rounded-full transition-all',
                              getAlignmentBarColor(alignment)
                            )}
                            style={{ width: `${alignment}%` }}
                          />
                        </div>
                      </div>

                      {/* Yield preview */}
                      <p className="text-muted-foreground mt-2 text-xs">
                        ~{formatMoney(bloc.baseYieldPerAP)}/AP
                      </p>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Intensity Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Intensity</Label>
              <span className="text-sm font-medium">{intensity} AP</span>
            </div>
            <Slider
              value={[intensity]}
              onValueChange={([value]) => setIntensity(value)}
              min={1}
              max={Math.min(3, availableAP)}
              step={1}
              disabled={availableAP < 1}
            />
            <div className="text-muted-foreground flex justify-between text-xs">
              <span>1 - Casual</span>
              <span>2 - Active</span>
              <span>3 - Major</span>
            </div>
            <p className="text-muted-foreground text-xs">
              {FUNDRAISE_INTENSITY_LABELS[intensity]}
            </p>
          </div>

          {/* Results Preview */}
          {selectedBloc && yieldEstimate && (
            <div className="bg-muted/50 space-y-3 rounded-lg border p-4">
              <h4 className="text-sm font-medium">Estimated Results</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expected yield:</span>
                  <span className="font-medium">
                    {formatMoney(yieldEstimate.min)} -{' '}
                    {formatMoney(yieldEstimate.max)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current funds:</span>
                  <span className="font-medium">
                    {formatMoney(party?.money ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Projected:</span>
                  <span className="font-medium text-green-600">
                    ~{formatMoney((party?.money ?? 0) + yieldEstimate.expected)}
                  </span>
                </div>
              </div>

              {/* Alignment context */}
              <p className="text-muted-foreground border-t pt-2 text-xs">
                {blocAlignments[selectedBloc] >= 75 ? (
                  <>Your party ideology strongly aligns with this donor group.</>
                ) : blocAlignments[selectedBloc] >= 50 ? (
                  <>Your party has decent alignment with this donor group.</>
                ) : blocAlignments[selectedBloc] >= 25 ? (
                  <>
                    Your party's ideology only partially matches this donor
                    group's preferences.
                  </>
                ) : (
                  <>
                    Your party's ideology conflicts with this donor group.
                    Expect lower yields.
                  </>
                )}
              </p>
            </div>
          )}

          {/* AP Summary */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">AP after action:</span>
            <span
              className={cn('font-medium', apAfterAction < 0 && 'text-destructive')}
            >
              {availableAP} → {Math.max(0, apAfterAction)}
            </span>
          </div>

          {/* Error display */}
          {error && (
            <p className="text-destructive text-sm">
              {error.message || 'Failed to queue action'}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={isCreating}
          >
            Back
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isCreating ? 'Queuing...' : 'Queue Action'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
